import styled from 'styled-components';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { BackgroundType } from '../../../types';
import { getClassName, useTemperature } from '../../helpers';
import type { ForecastListItem, RequestResponse } from '../api/request';
import { Icon } from '../common/icon';

type ForecastItemProps = {
  data: ForecastListItem;
  city: RequestResponse<ForecastListItem>['city'];
};

export const ForecastItem = ({ data, city }: ForecastItemProps) => {
  const { backgroundColor, backgroundType, language } = useSettings();
  const {
    main: { temp_max },
    dt,
    weather: [currentWeather]
  } = data;

  const { timezone } = city;
  const { icon } = currentWeather;
  const { language: locale } = language ?? {};

  const shiftedDate = new Date((dt + timezone) * 1000);
  const time = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: 'UTC'
  }).format(shiftedDate);

  const { processedTemperature } = useTemperature({ temp: temp_max });

  return (
    <Component>
      <DateContainer
        _backgroundType={backgroundType}
        _backgroundColor={backgroundColor}
      >
        {time}
      </DateContainer>

      <IconContainer>
        <Icon name={icon} />
      </IconContainer>

      <Temp _backgroundType={backgroundType} _backgroundColor={backgroundColor}>
        {processedTemperature}
      </Temp>
    </Component>
  );
};

const Component = styled.div.attrs({
  className: getClassName('forecast-item', 'component'),
  tabIndex: -1
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DateContainer = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
}>({
  className: getClassName('forecast-item', 'date')
})`
  white-space: nowrap;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  line-height: 1.39;

  color: ${({ _backgroundType, _backgroundColor }) =>
    _backgroundType === 'color' && _backgroundColor
      ? getBrightInvertedColor(_backgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : '#ffffff'};
`;

const IconContainer = styled.div.attrs({
  className: getClassName('forecast-item', 'icon')
})`
  width: 32px;
  height: 32px;
  margin-top: 4px;
`;

const Temp = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
}>({
  className: getClassName('forecast-item', 'date')
})`
  margin-top: 4px;
  text-align: center;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.39;

  color: ${({ _backgroundType, _backgroundColor }) =>
    _backgroundType === 'color' && _backgroundColor
      ? getBrightInvertedColor(_backgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : '#ffffff'};
`;
